import { FC } from "react";
import StatusBtn from "../../../components/UI/StatusBtn";
import { formatDateToDDMMYYYY } from "../../../utils/date_formatter";
import { CLIENT_TYPE_INDIVIDUAL } from "../../../constants/client";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { IColumn } from "../../../components/UI/CustomTable/IColumn.interface";
import HoverPopover from "../../../components/UI/HoverPopover";
import uniqueId from "../../../utils/generateId";

export const styledTypography = {
  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
    fontWeight: "bold",
  },
};

export const ClientDetailsLink = ({
  id,
  name,
}: {
  id: string;
  name: string;
}) => {
  const navigate = useNavigate();
  return (
    <Typography
      sx={styledTypography}
      color={"primary"}
      variant="h6"
      onClick={() => {
        navigate(`/clients/client-profile/${id}`);
      }}
    >
      {name}
    </Typography>
  );
};

const TagsCapsule = (appliesTo: string, index: any) => {
  return (
    <Box key={index} sx={{ padding: "5px 2px", display: "inline-block" }}>
      <StatusBtn title={appliesTo} type={4} />
    </Box>
  );
};

const SowTagsCapsule = (clientTags: string[]) => {
  return (
    <>
      {clientTags.length >= 1 ? TagsCapsule(clientTags[0], uniqueId()) : ""}
      {clientTags.length > 1 ? (
        <HoverPopover
          Trigger={(props: any) => (
            <StatusBtn
              title={`+${clientTags.length - 1}`}
              type={4}
              {...props}
            />
          )}
          Content={(() => (
            <Box sx={{ padding: "10px 20px", maxWidth: "250px" }}>
              {clientTags.slice(1).map((tag: string, index: any) => (
                <Box
                  key={index}
                  sx={{ padding: "5px 2px", display: "inline-block" }}
                >
                  <StatusBtn title={tag} type={4} />
                </Box>
              ))}
            </Box>
          ))()}
        />
      ) : (
        ""
      )}
    </>
  );
};

const ClientColumnShape =
  (ViewAction: FC<{ id: string }>) =>
  (clientType: string): IColumn[] => [
    {
      Header: "Name",
      accessor: "first_name",
      columnKey: "name",
      minWidth: 200,
      Cell: ({ row }: any) => {
        const { first_name, last_name, shared_name, id } = row.original;

        const name = getName(
          { first_name, last_name, shared_name },
          clientType,
        );
        return <ClientDetailsLink id={id} name={name} />;
      },
      columnBgColor: ({ row }: any) =>
        row?.original?.is_dormant ? "#ffc5c18f" : "inherit",
    },
    {
      Header: "Email",
      accessor: "email",
      columnKey: "email",
      minWidth: 150,
      Cell: ({ row }: any) => {
        const { email } = row.original;
        return email || "";
      },
      columnBgColor: ({ row }: any) =>
        row?.original?.is_dormant ? "#ffc5c18f" : "inherit",
    },
    {
      Header: "Branch",
      accessor: "branch_name",
      columnKey: "branch_name",
      minWidth: 150,
      Cell: ({ row }: any) => {
        return row.original?.branch_name || "";
      },
      columnBgColor: ({ row }: any) =>
        row?.original?.is_dormant ? "#ffc5c18f" : "inherit",
    },
    {
      Header: "Status",
      accessor: "is_active",
      columnKey: "status",
      minWidth: 150,
      Cell: ({ row }: any) => {
        const { is_active } = row.original;
        return (
          <StatusBtn
            title={is_active ? "Active" : "Inactive"}
            type={is_active ? 1 : 0}
          />
        );
      },
      columnBgColor: ({ row }: any) =>
        row?.original?.is_dormant ? "#ffc5c18f" : "inherit",
    },
    {
      Header: "Membership",
      accessor: "member_client",
      columnKey: "member_client",
      minWidth: 150,
      Cell: ({ row }: any) => {
        const { member_client } = row.original;
        return (
          <StatusBtn
            title={member_client === "yes" ? "Member" : "Not Member"}
            type={4}
          />
        );
      },
      columnBgColor: ({ row }: any) =>
        row?.original?.is_dormant ? "#ffc5c18f" : "inherit",
    },
    {
      Header: "Contact",
      accessor: "primarycellphone",
      columnKey: "primarycellphone",
      minWidth: 150,
      Cell: ({ row }: any) => {
        const { primarycellphone } = row.original;
        return primarycellphone || "";
      },
      columnBgColor: ({ row }: any) =>
        row?.original?.is_dormant ? "#ffc5c18f" : "inherit",
    },
    {
      Header: "Joining Date",
      accessor: "registration_date",
      columnKey: "registration_date",
      minWidth: 150,
      Cell: ({ row }: any) => {
        const { registration_date } = row.original;
        return formatDateToDDMMYYYY(registration_date);
      },
      columnBgColor: ({ row }: any) =>
        row?.original?.is_dormant ? "#ffc5c18f" : "inherit",
    },
    {
      Header: "Tags",
      accessor: "client_tags",
      columnKey: "client_tags",
      minWidth: 150,
      Cell: ({ row }: any) => {
        const tags = row?.original?.client_tags;
        if (!tags) return "";

        const clientListtags = tags
          .split(", ")
          .filter((tag: string) => tag.trim());
        return SowTagsCapsule(clientListtags);
      },
      columnBgColor: ({ row }: any) =>
        row?.original?.is_dormant ? "#ffc5c18f" : "inherit",
    },

    {
      Header: "Action",
      accessor: "action",
      columnKey: "action",
      minWidth: 100,
      maxWidth: 100,
      Cell: ({ row }: any) => {
        const data = row.original;
        return <ViewAction id={data.id} />;
      },
      columnBgColor: ({ row }: any) =>
        row?.original?.is_dormant ? "#ffc5c18f" : "inherit",
    },
  ];

export default ClientColumnShape;

export function getName(user_details: any, clientType: string) {
  if (clientType === CLIENT_TYPE_INDIVIDUAL) {
    return `${user_details?.first_name} ${user_details?.last_name}`;
  } else {
    return `${user_details?.shared_name}`;
  }
}
